import gsap from 'gsap';
import { html } from '@resn/gozer-core';
import { autobind } from 'core-decorators';
import { Component, componentMixin } from '@resn/gozer-core/component';
import { idleCallback } from 'utils/tick';

import env from 'core/env';
import AssetsManager, { initCDN } from 'utils/loading/assets-manager';
import DomComponentMixin from '@resn/gozer-core-mixins/dom-component';
import AssetsMixin from 'utils/loading/assets-mixin';
import MouseService from '@resn/gozer-services/mouse-service';
import ResizeService from '@resn/gozer-services/resize-service';
import WithRouter from '@resn/gozer-core-mixins/with-router';
import TickerService from '@resn/gozer-services/ticker-service';
import isSandbox from 'utils/is-sandbox';
import { isAndroid, isMobile } from '@resn/gozer-env';
import { SIZES, COLORS } from 'core/constants';
import { PAGE_DATA } from 'data/page-data';
import ScrollService from 'utils/services/scroll-service';
import { PAGES } from 'data/page-data';
import getPixelRatio from 'utils/get-pixel-ratio';
import { isHandheld } from '@resn/gozer-env';

import Menu from 'component/menu';
import TransitionOverlay from 'component/transition-overlay';
import Renderer from 'component/gl/renderer';
import Wipe from 'component/wipe';
import { pane, urlSearch } from '@resn/gozer-misc';

import { deviceBenchmark } from 'utils/device-benchmark';
import { importPage } from 'utils/import/import-async';
import store from 'core/store';
import { Clock } from 'three';
import config from '@resn/gozer-core/config';
import StoreUI from '@resn/gozer-store-ui';
import RendererBatch from 'component/gl/renderer-batch';
import { randomHash } from '@resn/gozer-misc';

import RotateOverlay from 'component/rotate-overlay';
import CookieBar from 'component/cookie-bar';
import SidePanelXs1Package from 'component/side-panels/xs1-package';
import AudioManager from 'utils/audio-manager';
import { Router } from '@resn/gozer-core/router';
import { device } from 'utils/device';
import Analytics from '@resn/gozer-core/analytics';
import ShopifyService from 'api/shopify';

const template = () => html` <div class="root" ?fallback=${deviceBenchmark.fallback}>
    <div data-ui="loginOverlay" class="stickyContent loginOverlay loginOverlay__hidden"></div>
    <div data-ui="stickyContent" id="stickyContent" class="stickyContent"></div>
</div>`;

const documentStyle = window.getComputedStyle(document.documentElement);

let fpsGraph;
if (urlSearch.get('fps') && pane) {
    import('@tweakpane/plugin-essentials').then(async (EssentialsPlugin) => {
        (await pane).registerPlugin(EssentialsPlugin);
        fpsGraph = (await pane).addBlade({
            view: 'fpsgraph',
            label: 'fpsgraph',
            lineCount: 2,
        });
    });
}

const preloadAssets = {
    // arrowsCombined: 'static/images/general/arrows-combined.svg#img',
    // homeHero: 'static/images/pages/home/hero-xp1.png#imgo',
    // oxefitHero: 'static/images/pages/oxefit/oxefit-hero-phone.png#imgo',
    // oxefitIcon: 'static/images/pages/oxefit/oxefit-icon.svg#img',
    // oxeleadHero: 'static/images/pages/oxelead/hero-oxelead-device.png#imgo',
    // oxeleadIcon: 'static/images/pages/oxelead/oxelead-icon.svg#img',
    // oxelifeHero: 'static/images/pages/oxelife/oxelife-hero-phone.png#imgo',
    // oxelifeIcon: 'static/images/pages/oxelife/oxelife-icon.svg#img',
    // preorderHero: 'static/images/pages/preorder/preorder-xp1-hero.png#imgo',
    // ...(deviceBenchmark.fallback
    //     ? {
    //           xp1Hero: 'static/images/pages/home/hero-xp1.png#imgo',
    //           xp1Triangle: 'static/images/pages/xp1/triangle-down-blue.svg#img',
    //       }
    //     : {}),
};

@componentMixin(AssetsMixin('preload'))
@componentMixin(WithRouter(store))
@componentMixin(DomComponentMixin)
class Shell extends Component {
    options = {
        template,
        appendTo: document.body,
    };

    props = {
        active: true,
        fullScreenVideoActive: () => store().state.fullScreenVideo.active,
        muted: () => store().state.muted,
        cursor: () => store().state.cursor,
    };

    assets = {
        ...(isSandbox() ? {} : preloadAssets),
    };

    async onSetup() {
        document.body.classList.add(device);

        initCDN(env.CDN);

        if (isAndroid) MouseService.patchAndroid();

        if (config.showStore && __DEV__) {
            await new StoreUI().setup({
                options: { store: store(), appendTo: document.body },
                props: { store: () => store() },
            });
        }

        // setup common components
        await this.addComponents();

        this.addListeners();

        this.resize();

        if (__DEV__) window.shell = this;
    }

    async onAfterSetup() {
        // preload all manifests needed to load next view
        // AssetsManager.preloadAll(this.onPreloaderProgress);

        if (!isSandbox()) await AssetsManager.preloadManifest('preload', this.onPreloaderProgress);
        AssetsManager.preloadAll();

        // trackPageLoad();

        // wait for preloader tasks
        if (this.options.whilePreloading) {
            await this.options.whilePreloading();
        }

        // clock
        this.clock = new Clock();

        // add render loop
        TickerService.add(this.render);

        // // wait for preload to finish
        // await preloadPromise;

        // hide and destroy preloader
        if (this.options.preloader) await this.options.preloader.hide();

        // current page is on screen
        if (this.page) {
            // const dur = 1.5;
            // const dir = -1;
            // const fromY = true;
            // await this.transitionOverlay.show({ dur: 0 });
            // this.transitionOverlay.hide({ dur, dir, fromY });
            // await idleCallback();
            // this.page.enter({ dir, dur, fromY });
            // await idleCallback();

            if (this.wipe) gsap.timeline({}).add(this.wipe.getAnimation(false, '0%'), 0);
            // if (this.menu) this.menu.props.visible = true;

            const { page } = store().state.route;
            const dur = page === PAGE_DATA.HOME.name || page === PAGE_DATA.PREORDER.name ? 0 : 2;

            await idleCallback();
            this.page.enter({ fromY: true, dir: -1, delay: 0.1, dur });
        }
    }

    /**
     * Preloader
     * @param {number} progress value from 0 to 1
     */

    @autobind
    onPreloaderProgress(progress) {
        if (this.options.preloader) this.options.preloader.props.progress = progress * 100;
    }

    /**
     * Components
     */

    async addComponents() {
        this.renderer = await new Renderer().setup({
            options: {
                appendTo: this.ui.stickyContent,
            },
        });

        // Jamie : Next loader version...
        // initLoadTexture(this.renderer.getRenderer());
        // initLoadCompressed(this.renderer.getRenderer());

        // use renderers batch on mobile for multi renderer scrolling
        if (isHandheld) {
            await new RendererBatch().setup({
                options: {
                    batch: await Promise.all(
                        Array.from(new Array(3)).map(async (_, i) => {
                            return await new Renderer().setup({
                                options: { i, uuid: randomHash(), isSingleton: false, useViewportSize: false },
                                props: {},
                            });
                        }),
                    ),
                },
                props: {},
            });
        }

        if (isSandbox()) return;

        this.menu = await new Menu().setup({ options: { appendTo: this.el } });
        this.transitionOverlay = await new TransitionOverlay().setup({ options: { appendTo: this.el } });
        this.rotateOverlay = await new RotateOverlay().setup({ options: { appendTo: this.el } });
        this.cookieBar = await new CookieBar().setup({ options: { appendTo: this.el } });
        this.wipe = await new Wipe().setup({ options: { appendTo: this.el, color: COLORS.DARK_BLUE } });

        this.xs1Packages = await new SidePanelXs1Package().setup({
            options: {
                appendTo: this.el,
                onClose: () => {
                    store().state.panels.xs1Open = false;
                },
            },
            props: {
                open: () => store().state.panels.xs1Open,
            },
        });
    }

    /**
     * Event listeners
     */

    addListeners() {
        ResizeService.add(this.resize);
    }

    removeListeners() {
        ResizeService.remove(this.resize);
    }

    /**
     * Dispatched when navigation is updated
     * @param {proxy} route
     * @param {boolean} firstTime
     */

    async onNavigationUpdate(route, firstTime, popstate) {
        // block re-instantiation of pages if navigation is to same route
        if (route.match.page === String(route.previous.match && route.previous.match.page)) {
            return;
        }

        /** Show the login overlay for login screen entry. */
        if (route.match.page === 'signin-oxefit') {
            this.ui.loginOverlay.classList.remove('loginOverlay__hidden');
        } else {
            this.ui.loginOverlay.classList.add('loginOverlay__hidden');
        }

        if (route.match.page === 'account' && ShopifyService.isCartEmpty()) {
            Router.navigate('/cart');
            return;
        }

        if (!firstTime) Analytics.trackPage(route.match.page);

        store().state.route.first = firstTime;
        store().state.navigating = true;

        const prevPage = this.page;
        const isMenuOpen = this.menu && this.menu.props.open;

        let dir = 0;
        let dur = 0;
        const isOxePageToOxePage =
            route.match.page.includes('oxe') && (prevPage ? route.previous.match.page.includes('oxe') : false);

        // leave current page
        // if (!popstate && prevPage) {
        if (prevPage) {
            const currentIndex = PAGES.findIndex((page) => page.name === route.match.page);
            const prevIndex = PAGES.findIndex((page) => page.name === route.previous.match.page);

            dir = Math.sign(currentIndex - prevIndex);
            dir = isMenuOpen ? 0 : dir;
            dur = isMenuOpen ? 0 : 0.8;

            prevPage.leave({ dir, dur });
            // prevPage.leave({ dir, dur }).then(() => {
            //     prevPage.destroy();
            // });
        }

        if (this.transitionOverlay && prevPage) {
            if (isMenuOpen) {
                prevPage.destroy();
                this.menu.props.open = false;
            } else {
                const transitionBgColor = isOxePageToOxePage ? COLORS.OFF_WHITE : COLORS.DARK_BLUE;
                this.transitionOverlay.props.bgColor = transitionBgColor;

                await this.transitionOverlay.show({ dir, dur });
                prevPage.destroy();

                this.page = null;
            }
        }

        const leaveDur = 1;
        const nextPage = await this.setupNextPage(route.match.page, firstTime);
        this.page = nextPage;

        ScrollService.setScroll(0);
        nextPage.resize();

        await idleCallback();

        if (this.transitionOverlay && !isMenuOpen && !firstTime) this.transitionOverlay.hide({ dir, dur: leaveDur });

        // enter new page
        if (!firstTime) {
            await nextPage.enter({ dir, dur: leaveDur });
            if (Object.keys(AssetsManager.manifests).length > 0) {
                AssetsManager.preloadAll();
            }
        }

        store().state.navigating = false;
    }

    async setupNextPage(pageId, firstTime) {
        // create next page
        const PageComponent = await importPage(pageId);
        const page = await new PageComponent.default().setup({
            options: {
                id: pageId,
                appendTo: this.el,
            },
        });
        // if (page.el) page.el.style.opacity = 0;

        return page;
    }

    setFullScreenVideoActive(bool, { firstTrigger }) {
        if (firstTrigger) return;

        if (this.page) this.page.props.active = !bool;

        store().state.scrollLock = bool;
    }

    /**
     *
     * @param {Cursor} cursor
     */

    setCursor(cursor) {
        switch (cursor) {
            case 'none':
                document.body.setAttribute('cursor', 'none');
                break;
            case 'pointer':
                document.body.setAttribute('cursor', 'pointer');
                break;
            case 'grab':
                document.body.setAttribute('cursor', 'grab');
                break;
            default:
                document.body.removeAttribute('cursor');
                break;
        }
    }

    setMuted(val) {
        val ? AudioManager.fadeOut() : AudioManager.fadeIn();
    }

    /**
     * Render loop
     */

    @autobind
    render() {
        if (fpsGraph) fpsGraph.begin();
        if (this.clock) {
            store().state.clock.delta = this.clock.getDelta();
            store().state.clock.time += store().state.clock.delta;
        }
        // if (this.page && this.page.render) this.page.render(delta);
        if (fpsGraph) fpsGraph.end();
    }

    /**
     * Resize
     */

    @autobind
    resize() {
        const { state } = store();

        const { width, height } = ResizeService.size;
        const { width: wPrev } = state.viewport;

        // block resizing call if only the width has changed on mobile (meaning only the browser ui has changed) ;
        if (width === wPrev && isMobile) return;

        state.viewport.width = width;
        state.viewport.height = height;
        state.viewport.pr = getPixelRatio();
        state.scale = parseFloat(documentStyle.fontSize) / 10;

        state.mobile = state.viewport.width <= SIZES.MOBILE_THRESHOLD;
        state.desktop = state.viewport.width > SIZES.MOBILE_THRESHOLD;

        if (this.menu) this.menu.resize();
        if (this.page) this.page.resize();
    }

    /**
     * Destroy
     */

    onDestroy() {
        this.removeListeners();

        if (this.pulseDelay) this.pulseDelay.kill();
    }
}

export default Shell;
